// Achievement definitions and pure unlock logic. Reuses the same per-feature
// counts the levels/XP system already computes (see xp.ts) rather than
// reading progress snapshots a second time - an achievement is just a
// threshold on a count the app already has. No new AWS/CDK surface: this is
// entirely derived from existing client-trusted progress, matching the XP
// system's own architecture decision (see CLAUDE.md).
//
// An achievement unlocks in one of two ways: a feature-count threshold (the
// original rule, `feature` + `minCount`) or by completing a mission (`missionId`,
// see missions.ts - computed from the raw progress snapshots, since a mission
// is about WHICH items are done, not just how many).
import { isMissionComplete } from "./missions";
import { ProgressSnapshots, XpBreakdown } from "./xp";

export type AchievementIcon =
    | "pencil"
    | "glasses"
    | "book"
    | "cap"
    | "feather"
    | "nib"
    | "brush"
    | "headphones"
    | "earbuds"
    | "ear";

type AchievementBase = {
    id: string;
    title: string;
    // Doubles as the badge's tooltip/aria-label text.
    description: string;
    icon: AchievementIcon;
};

export type FeatureAchievement = AchievementBase & {
    feature: XpBreakdown["entries"][number]["feature"];
    minCount: number;
    missionId?: undefined;
};

export type MissionAchievement = AchievementBase & {
    missionId: string;
    feature?: undefined;
    minCount?: undefined;
};

export type Achievement = FeatureAchievement | MissionAchievement;

export const ACHIEVEMENTS: Achievement[] = [
    {
        id: "first-lesson",
        title: "First Steps",
        description: "Completed your first lesson",
        icon: "pencil",
        feature: "lessonProgress",
        minCount: 1,
    },
    {
        id: "first-kanji",
        title: "Kanji Beginner",
        description: "Marked your first kanji as known",
        icon: "glasses",
        feature: "kanjiMemory",
        minCount: 1,
    },
    {
        id: "first-word",
        title: "Word Collector",
        description: "Learned your first vocabulary word",
        icon: "book",
        feature: "vocabularyProgress",
        minCount: 1,
    },
    {
        id: "first-grammar-point",
        title: "Grammar Scholar",
        description: "Learned your first grammar point",
        icon: "cap",
        feature: "grammarProgress",
        minCount: 1,
    },
    {
        id: "hiragana-artist",
        title: "Hiragana Artist",
        description: "Drew all 46 basic hiragana",
        icon: "feather",
        missionId: "draw-all-hiragana",
    },
    {
        id: "katakana-artist",
        title: "Katakana Artist",
        description: "Drew all 46 basic katakana",
        icon: "nib",
        missionId: "draw-all-katakana",
    },
    {
        id: "n5-kanji-artist",
        title: "N5 Kanji Artist",
        description: "Drew every N5 kanji",
        icon: "brush",
        missionId: "draw-all-n5-kanji",
    },
    {
        id: "hiragana-listener",
        title: "Hiragana Listener",
        description: "Listened to all 46 basic hiragana",
        icon: "headphones",
        missionId: "listen-all-hiragana",
    },
    {
        id: "katakana-listener",
        title: "Katakana Listener",
        description: "Listened to all 46 basic katakana",
        icon: "earbuds",
        missionId: "listen-all-katakana",
    },
    {
        id: "n5-kanji-listener",
        title: "N5 Kanji Listener",
        description: "Listened to every N5 kanji",
        icon: "ear",
        missionId: "listen-all-n5-kanji",
    },
];

function isUnlocked(
    achievement: Achievement,
    countByFeature: Map<string, number>,
    snapshots: ProgressSnapshots
): boolean {
    if (achievement.missionId !== undefined) {
        // An unknown missionId is simply never complete (see isMissionComplete).
        return isMissionComplete(achievement.missionId, snapshots);
    }
    return (countByFeature.get(achievement.feature) || 0) >= achievement.minCount;
}

// Which of ACHIEVEMENTS the given breakdown (and, for mission achievements,
// the raw progress snapshots the breakdown was computed from) currently
// qualifies for - order follows ACHIEVEMENTS, not unlock order (this module
// has no notion of "when"; see useAchievementsSeen for detecting
// newly-unlocked ones). `snapshots` is optional so callers that only have a
// breakdown keep working; mission achievements then simply stay locked.
export function computeUnlockedAchievements(
    breakdown: XpBreakdown,
    snapshots: ProgressSnapshots = {}
): Achievement[] {
    const countByFeature = new Map<string, number>(
        breakdown.entries.map((entry) => [entry.feature, entry.count])
    );
    return ACHIEVEMENTS.filter((achievement) => isUnlocked(achievement, countByFeature, snapshots));
}
